import { listToLower } from '@/library/array-helper';
import { Stadsdeel, mapNamen } from '@/repositories/stadsdelenRepository';
import filter, { FilterValues } from '@/store/filter';
import { Ondersteuningsaanbod, OndersteuningsSubaanbod, School } from '@/types';

/**
 * Most of the filterOptions will be fetched from the database. however OCO
 * wants to have filter fields with custom logic. This file contains for each
 * filterKey the custom fields with their corresponding handler logic. Note
 * that a filterKey should be a field present on the school.
 *
 *  @function handler
 *  returns
 *   - TRUE it should keep the school in the list
 *   - FALSE the school should not be shown
 */
export default {
  type: [
    { value: 'Alle schoolsoorten', handler() { return true; } },
  ],
  schooladvies: [
    { value: 'Alle adviezen', handler() { return true; } },
    { value: 'pro', handler(schoolValue: string | null) { return schoolValue?.includes('praktijkonderwijs'); } },
    {
      value: 'vmbo-b + lwoo',
      handler(schoolValue: string | null) {
        return ['vmbo-b', 'lwoo'].every((schoolAdvies) => schoolValue?.includes(schoolAdvies));
      },
    },
    {
      value: 'vmbo-b',
      handler(schoolValue: string | null) {
        return ['vmbo-b']
          .every((schoolAdvies) => schoolValue?.includes(schoolAdvies));
      },
    },
    {
      value: 'vmbo-b/k + lwoo',
      handler(schoolValue: string | null) {
        return ['vmbo-b', 'vmbo-k', 'lwoo']
          .every((schoolAdvies) => schoolValue?.includes(schoolAdvies));
      },
    },
    {
      value: 'vmbo-b/k',
      handler(schoolValue: string | null) {
        return ['vmbo-b', 'vmbo-k']
          .every((schoolAdvies) => schoolValue?.includes(schoolAdvies));
      },
    },
    {
      value: 'vmbo-k + lwoo',
      handler(schoolValue: string | null) {
        return ['vmbo-k', 'lwoo']
          .every((schoolAdvies) => schoolValue?.includes(schoolAdvies));
      },
    },
    {
      value: 'vmbo-k',
      handler(schoolValue: string | null) {
        return ['vmbo-k']
          .every((schoolAdvies) => schoolValue?.includes(schoolAdvies));
      },
    },
    {
      value: 'vmbo-t + lwoo',
      handler(schoolValue: string | null) {
        return ['vmbo-t', 'lwoo']
          .every((schoolAdvies) => schoolValue?.includes(schoolAdvies));
      },
    },
    {
      value: 'vmbo-t',
      handler(schoolValue: string | null) {
        return ['vmbo-t']
          .every((schoolAdvies) => schoolValue?.includes(schoolAdvies));
      },
    },
    {
      value: 'vmbo-t/havo',
      handler(schoolValue: string | null) {
        return ['vmbo-t', 'havo']
          .every((schoolAdvies) => schoolValue?.includes(schoolAdvies));
      },
    },
    {
      value: 'havo',
      handler(schoolValue: string | null) {
        return ['havo']
          .every((schoolAdvies) => schoolValue?.includes(schoolAdvies));
      },
    },
    {
      value: 'havo/vwo',
      handler(schoolValue: string | null) {
        return ['havo', 'vwo']
          .every((schoolAdvies) => schoolValue?.includes(schoolAdvies));
      },
    },
    {
      value: 'vwo',
      handler(schoolValue: string | null) {
        return ['vwo']
          .every((schoolAdvies) => schoolValue?.includes(schoolAdvies));
      },
    },
  ],
  soortonderwijs: [
    {
      type: 'default',
      value: 'Breed of categoraal',
      handler() {
        return true;
      },
    },
    {
      type: 'breed',
      value: 'pro-vmbo-b-k',
      handler(schoolValue: string) {
        const mustHaves = ['praktijkonderwijs', 'vmbo-b', 'vmbo-k'];
        const couldHaves = ['lwoo', 'vmbo-g'];
        return isValidMoscow(schoolValue.split(','), mustHaves, couldHaves);
      },
    },
    {
      type: 'breed',
      value: 'pro-vmbo-b-k-t',
      handler(schoolValue: string) {
        const mustHaves = ['praktijkonderwijs', 'vmbo-b', 'vmbo-k', 'vmbo-t'];
        const couldHaves = ['lwoo', 'vmbo-g'];
        return isValidMoscow(schoolValue.split(','), mustHaves, couldHaves);
      },
    },
    {
      type: 'breed',
      value: 'vmbo-b-k',
      handler(schoolValue: string) {
        const mustHaves = ['vmbo-b', 'vmbo-k'];
        const couldHaves = ['lwoo', 'vmbo-g'];
        return isValidMoscow(schoolValue.split(','), mustHaves, couldHaves);
      },
    },
    {
      type: 'breed',
      value: 'vmbo-b-k-t',
      handler(schoolValue: string) {
        const mustHaves = ['vmbo-b', 'vmbo-k', 'vmbo-t'];
        const couldHaves = ['lwoo', 'vmbo-g'];
        return isValidMoscow(schoolValue.split(','), mustHaves, couldHaves);
      },
    },
    {
      type: 'breed',
      value: 'vmbo-b-k-t-havo-vwo (ath)',
      handler(schoolValue: string) {
        const mustHaves = ['vmbo-b', 'vmbo-k', 'vmbo-t', 'havo', 'vwo (atheneum)'];
        const couldHaves = ['lwoo', 'vmbo-g'];
        return isValidMoscow(schoolValue.split(','), mustHaves, couldHaves);
      },
    },
    {
      type: 'breed',
      value: 'vmbo-t-havo',
      handler(schoolValue: string) {
        const mustHaves = ['vmbo-t', 'havo'];
        const couldHaves = ['lwoo', 'vmbo-g'];
        return isValidMoscow(schoolValue.split(','), mustHaves, couldHaves);
      },
    },
    {
      type: 'breed',
      value: 'vmbo-t-havo-vwo (ath)',
      handler(schoolValue: string) {
        const mustHaves = ['vmbo-t', 'havo', 'vwo (atheneum)'];
        const couldHaves = ['lwoo', 'vmbo-g'];
        return isValidMoscow(schoolValue.split(','), mustHaves, couldHaves);
      },
    },
    {
      type: 'breed',
      value: 'vmbo-t-havo-vwo (ath en gym)',
      handler(schoolValue: string) {
        const mustHaves = ['vmbo-t', 'havo', 'vwo (atheneum)', 'vwo (gymnasium)'];
        const couldHaves = ['lwoo', 'vmbo-g'];
        return isValidMoscow(schoolValue.split(','), mustHaves, couldHaves);
      },
    },
    {
      type: 'breed',
      value: 'havo-vwo (ath)',
      handler(schoolValue: string) {
        const mustHaves = ['havo', 'vwo (atheneum)'];
        return isValidMoscow(schoolValue.split(','), mustHaves, []);
      },
    },
    {
      type: 'breed',
      value: 'havo-vwo (ath en gym)',
      handler(schoolValue: string) {
        const mustHaves = ['havo', 'vwo (atheneum)', 'vwo (gymnasium)'];
        return isValidMoscow(schoolValue.split(','), mustHaves, []);
      },
    },
    {
      type: 'categoraal',
      value: 'pro',
      handler(schoolValue: string) {
        const mustHaves = ['praktijkonderwijs'];
        return isValidMoscow(schoolValue.split(','), mustHaves, []);
      },
    },
    {
      type: 'categoraal',
      value: 'vmbo-t',
      handler(schoolValue: string) {
        const mustHaves = ['vmbo-t'];
        const couldHaves = ['lwoo', 'vmbo-g'];
        return isValidMoscow(schoolValue.split(','), mustHaves, couldHaves);
      },
    },
    {
      type: 'categoraal',
      value: 'havo',
      handler(schoolValue: string) {
        const mustHaves = ['havo'];
        return isValidMoscow(schoolValue.split(','), mustHaves, []);
      },
    },
    {
      type: 'categoraal',
      value: 'vwo (ath en gym)',
      handler(schoolValue: string) {
        const mustHaves = ['vwo (atheneum)', 'vwo (gymnasium)'];
        return isValidMoscow(schoolValue.split(','), mustHaves, []);
      },
    },
    {
      type: 'categoraal',
      value: 'vwo (gym)',
      handler(schoolValue: string) {
        const mustHaves = ['vwo (gymnasium)'];
        return isValidMoscow(schoolValue.split(','), mustHaves, []);
      },
    },
  ] as soortonderwijsExtension[],
  stadsdeel: [
    {
      value: 'Heel Amsterdam',
      wantsData: true,
      handler(schoolValue: string | null, school: School, data: Stadsdeel[]) {
        if (!schoolValue) return false;
        const lowerCaseSchoolStadsdeel = schoolValue.toLowerCase();
        return listToLower(mapNamen(data))
          .includes(lowerCaseSchoolStadsdeel);
      },
    },
    {
      value: 'Buiten Amsterdam',
      wantsData: true,
      handler(schoolValue: string | null, school: School, data: Stadsdeel[]) {
        if (!schoolValue) return true;

        return !(
          listToLower(mapNamen(data))
            .includes(schoolValue.toLowerCase())
        );
      },
    },
    {
      value: 'Alle scholen',
      handler() {
        return true;
      },
    },
  ],
  denominatie: [
    { value: 'Alle bekostigde richtingen', handler(schoolValue: string) { return schoolValue !== 'particulier'; } },
    { value: 'Alle scholen', handler() { return true; } },
  ],
  onderwijsconcept: [
    {
      value: 'kleinschalig',
      handler(onderwijsconcept: string) { return onderwijsconcept.includes('kleinschalig'); },
      isInactive(filter: FilterValues) { return filter.type !== 'vo'; },
    },
    {
      value: 'talent',
      handler(onderwijsconcept: string) { return onderwijsconcept.includes('talent'); },
      isInactive(filter: FilterValues) { return filter.type !== 'vo'; },
    },
    { value: 'Alle onderwijsconcepten', handler() { return true; } },
  ],


  ondersteuningsaanbod: [
	{ value: 'volledig aanbod', handler() { return true; } },
	{
		value: "Remedial teaching",
		handler(ondersteuningsaanbod: Ondersteuningsaanbod[]) {
			return ondersteuningsaanbod.filter((aanbod) => {
				return aanbod.ondersteuningsaanbod.toLowerCase() === "remedial teaching";
			}).length > 0
		}
	},
	{
		value: "Toetstijd",
		handler(ondersteuningsaanbod: Ondersteuningsaanbod[]) {
			return ondersteuningsaanbod.filter((aanbod) => {
				return aanbod.ondersteuningsaanbod.toLowerCase() === "toetstijd";
			}).length > 0
		}
	},
	{
		value: "Aangepaste toetsen",
		handler(ondersteuningsaanbod: Ondersteuningsaanbod[]) {
			return ondersteuningsaanbod.filter((aanbod) => {
				return aanbod.ondersteuningsaanbod.toLowerCase() === "aangepaste toetsen";
			}).length > 0
		}
	},
	{
		value: "Digitale hulpmiddelen",
		handler(ondersteuningsaanbod: Ondersteuningsaanbod[]) {
			return ondersteuningsaanbod.filter((aanbod) => {
				return aanbod.ondersteuningsaanbod.toLowerCase() === "digitale hulpmiddelen";
			}).length > 0
		}
	},
	{
		value: "Schriftelijke hulpmiddelen",
		handler(ondersteuningsaanbod: Ondersteuningsaanbod[]) {
			return ondersteuningsaanbod.filter((aanbod) => {
				return aanbod.ondersteuningsaanbod.toLowerCase() === "schriftelijke hulpmiddelen";
			}).length > 0
		}
	},
	{
		value: "Overige ondersteuningsmogelijkheden",
		handler(ondersteuningsaanbod: Ondersteuningsaanbod[]) {
			return ondersteuningsaanbod.filter((aanbod) => {
				return aanbod.ondersteuningsaanbod.toLowerCase() === "overige ondersteuningsmogelijkheden";
			}).length > 0
		}
	},
	{
		value: "Deskundigen",
		handler(ondersteuningsaanbod: Ondersteuningsaanbod[]) {
			return ondersteuningsaanbod.filter((aanbod) => {
				return aanbod.ondersteuningsaanbod.toLowerCase() === "deskundigen";
			}).length > 0
		}
	},

  ],

  ondersteunings_subaanbod: [
	{ value: 'volledig subaanbod', handler() { return true; } },
	{
		value: "Individueel",
		parent: "remedial teaching",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "individueel"
			);
		}

	},
	{
		value: "In groepjes",
		parent: "remedial teaching",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "in groepjes"
			);
		}
	},
	{
		value: "Extra toetstijd",
		parent: "toetstijd",

		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "extra toetstijd"
			);
		}
	},
	{
		value: "Grote toetsen in twee delen maken",
		parent: "toetstijd",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "grote toetsen in twee delen maken"
			);
		}
	},
	{
		value: "Verlengde pauzes bij luistertoetsen",
		parent: "toetstijd",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "verlengde pauzes bij luistertoetsen"
			);
		}
	},
	{
		value: "Mondeling toetsen",
		parent: "aangepaste toetsen",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "mondeling toetsen"
			);
		}
	},
	{
		value: "Schriftelijke toets mondeling toelichten",
		parent: "aangepaste toetsen",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "schriftelijke toets mondeling toelichten"
			);
		}
	},
	{
		value: "Aangepaste spellingbeoordeling",
		parent: "aangepaste toetsen",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "aangepaste spellingbeoordeling"
			);
		}
	},
	{
		value: "Spelling controleren op laptop",
		parent: "aangepaste toetsen",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "spelling controleren op laptop"
			);
		}
	},
	{
		value: "Mondeling herkansen",
		parent: "aangepaste toetsen",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "mondeling herkansen"
			);
		}
	},
	{
		value: "Max 2 proefwerken per dag",
		parent: "aangepaste toetsen",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "max 2 proefwerken per dag"
			);
		}
	},
	{
		value: "Minder toets vragen",
		parent: "aangepaste toetsen",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "minder toets vragen"
			);
		}
	},
	{
		value: "Vrijstelling spellingtoetsen",
		parent: "aangepaste toetsen",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "vrijstelling spellingtoetsen"
			);
		}
	},
	{
		value: "Toetsen in een aparte ruimte",
		parent: "aangepaste toetsen",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "toetsen in een aparte ruimte"
			);
		}
	},
	{
		value: "Vergrote tekst",
		parent: "aangepaste toetsen",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "vergrote tekst"
			);
		}
	},
	{
		value: "Digitale oefensoftware",
		parent: "digitale hulpmiddelen",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "digitale oefensoftware"
			);
		}
	},
	{
		value: "Audio-ondersteuning",
		parent: "digitale hulpmiddelen",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "audio-ondersteuning"
			);
		}
	},
	{
		value: "Digitale schoolboeken",
		parent: "digitale hulpmiddelen",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "digitale schoolboeken"
			);
		}

	},
	{
		value: "Spellingcontrole op laptop",
		parent: "digitale hulpmiddelen",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "spellingcontrole op laptop"
			);
		}

	},
	{
		value: "Hulpkaarten",
		parent: "overige ondersteuningsmogelijkheden",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "hulpkaarten"
			);
		}
	},
	{
		value: "Voorleesbeurt voorbereiden",
		parent: "overige ondersteuningsmogelijkheden",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "voorleesbeurt voorbereiden"
			);
		}
	},

	{
		value: "Gebruik woordenboek",
		parent: "schriftelijke hulpmiddelen",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "gebruik woordenboek"
			);
		}
	},
	{
		value: "Plaatsing in ‘taalklas’",
		parent: "overige ondersteuningsmogelijkheden",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "plaatsing in ‘taalklas’"
			);
		}
	},
	{
		value: "Vooraan in de klas zitten",
		parent: "overige ondersteuningsmogelijkheden",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "vooraan in de klas zitten"
			);
		}
	},
	{
		value: "Geen onverwachte voorleesbeurt",
		parent: "overige ondersteuningsmogelijkheden",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "geen onverwachte voorleesbeurt"
			);
		}
	},
	{
		value: "Verminderde omvang boekenlijst",
		parent: "overige ondersteuningsmogelijkheden",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "verminderde omvang boekenlijst"
			);
		}
	},
	{
		value: "Geluiddempende koptelefoon",
		parent: "overige ondersteuningsmogelijkheden",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "geluiddempende koptelefoon"
			);
		}
	},
	{
		value: "Mondelinge instructie",
		parent: "overige ondersteuningsmogelijkheden",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "mondelinge instructie"
			);
		}
	},
	{
		value: "Extra leestijd",
		parent: "overige ondersteuningsmogelijkheden",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "extra leestijd"
			);
		}
	},
	{
		value: "Dyslexiepas",
		parent: "overige ondersteuningsmogelijkheden",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "dyslexiepas"
			);
		}
	},
	{
		value: "Huiswerkklas",
		parent: "overige ondersteuningsmogelijkheden",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "huiswerkklas"
			);
		}
	},
	{
		value: "Steunles Nederlands",
		parent: "overige ondersteuningsmogelijkheden",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "steunles nederlands"
			);
		}
	},
	{
		value: "Deskundigen",
		parent: "deskundigen",
		handler(ondersteunings_subaanbod: OndersteuningsSubaanbod[]) {
			return ondersteunings_subaanbod.some((aanbod) =>
				aanbod.naam.toLowerCase() === "deskundigen"
			);
		}
	}

  ],


  inspectieoordeel: [
    {
      value: 'Positief',
      handler(oSchoolValue: string | null) {
        if (!oSchoolValue) return false;
        const schoolValue = oSchoolValue.toLowerCase();
        // remove onvoldoendes since 'voldoende' is a substring of 'onvoldoende'
        if (schoolValue.replace(/onvoldoende/g, '').includes('voldoende')
          || schoolValue.includes('goed')) {
          return true;
        }
        return false;
      },
    },
    {
      value: 'Negatief',
      handler(oSchoolValue: string | null) {
        if (!oSchoolValue) return false;
        const schoolValue = oSchoolValue.toLowerCase();
        if (schoolValue.includes('zwak') || schoolValue.includes('onvoldoende')) {
          return true;
        }
        return false;
      },
    },
    {
      value: 'Onbekend',
      handler(oSchoolValue: string | null) {
        if (!oSchoolValue) return false;
        const schoolValue = oSchoolValue.toLowerCase();
        if (['zwak', 'voldoende', 'goed']
          .some((negPosOordeel) => schoolValue.includes(negPosOordeel))) {
          return false;
        }
        return true;
      },
    },
    { value: 'Alle inspectieoordelen', handler() { return true; } },
  ],
};


/**
 * MoSCoW helper function
 * of an array it will check if it is follows the moscow rules.
 *
 * @param mustHaves an array of items that should be in the array
 * @param couldHaves an array of items that may be in the array
 * @param wontHaves an array of items that should be excluded from the array
 */
function isValidMoscow(values = [] as string[], mustHaves = undefined as undefined | string[],
  couldHaves = undefined as undefined | string[],
  wontHaves = undefined as undefined | string[]): boolean {
  if (!values.length) {
    return true;
  }
  if (mustHaves) {
    for (const mustHave of mustHaves) {
      if (!values.includes(mustHave)) {
        return false;
      }
    }
  }
  if (couldHaves) {
    for (const value of values) {
      if (!couldHaves.includes(value) && !mustHaves?.includes(value)) {
        return false;
      }
    }
  }
  if (wontHaves) {
    for (const wontHave of wontHaves) {
      if (values.includes(wontHave)) { return false; }
    }
  }

  return true;
}

type soortonderwijsExtension = {
  value: string;
  type: 'breed' | 'categoraal' | 'default';
  handler: Function;
}
